import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Page, Text, Grid, Tooltip, SkeletonDisplayText, BlockStack, Button } from "@shopify/polaris";
import { ViewIcon } from "@shopify/polaris-icons";
import { useGetPaymentStatisticsQuery } from "../store/store/slices/Payment";
import DateRange from "../components/atoms/DateRange";
import CourierFilter from "../components/atoms/CourierFilter";
import dayjs from "dayjs";
import { AreaChart, Area, ResponsiveContainer } from "recharts";

// Shopify Polaris color palette
const POLARIS_COLORS = {
    primary: "#008060", // Success/Primary green
    info: "#5c6ac4", // Info blue
    critical: "#d72c0d", // Critical red
    warning: "#ffc453", // Warning yellow
    subduedBg: "#f6f6f7", // Subdued background
    border: "#e1e3e5", // Border color
    textSubdued: "#6d7175", // Subdued text
    surface: "#ffffff", // Surface white
};

// Mini sparkline component
const MiniSparkline = ({ data, color = POLARIS_COLORS.info }: { data: number[]; color?: string }) => {
    const chartData = data.map((value, index) => ({ value, index }));

    return (
        <div style={{ width: "80px", height: "40px" }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                    <Area
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        fill={color}
                        fillOpacity={0.2}
                        strokeWidth={1.5}
                        dot={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

// Generate sample data for sparkline
const generateSparklineData = (value: number) => {
    const base = value * 0.3;
    return [
        base * 0.5,
        base * 0.7,
        base * 0.9,
        base,
        base * 0.95,
        base * 0.98,
        base,
    ];
};

export default function FinancePage() {
    const navigateTo = useNavigate();
    const params = new URLSearchParams(window.location.search);

    // Get dates from URL params or use defaults (last 7 days)
    const getStartDate = () => {
        const bookingFrom = params.get("bookingfrom");
        if (bookingFrom) {
            return dayjs(new Date(bookingFrom)).format("YYYY-MM-DD");
        }
        return dayjs().subtract(6, "day").format("YYYY-MM-DD");
    };

    const getEndDate = () => {
        const bookingTo = params.get("bookingto");
        if (bookingTo) {
            return dayjs(new Date(bookingTo)).format("YYYY-MM-DD");
        }
        return dayjs().format("YYYY-MM-DD");
    };

    const startDate = getStartDate();
    const endDate = getEndDate();
    const courier = params.get("courier") || "";

    const { data: paymentStatistics, isLoading, isError, isFetching, refetch } = useGetPaymentStatisticsQuery({
        startDate,
        endDate,
        courier,
    });

    useEffect(() => {
        // Set default dates if not present
        if (!params.get("bookingfrom") || !params.get("bookingto")) {
            const defaultStart = dayjs().subtract(6, "day").format("YYYY-MM-DD");
            const defaultEnd = dayjs().format("YYYY-MM-DD");
            params.set("bookingfrom", new Date(defaultStart).toDateString());
            params.set("bookingto", new Date(defaultEnd).toDateString());
            navigateTo({ search: params.toString() });
        }
    }, []);

    const firstRowCards = [
        {
            title: "Total Orders",
            value: paymentStatistics?.totalOrders || 0,
            format: (val: number) => val.toLocaleString(),
            tooltip: "Total number of orders which are either paid or unpaid with following statuses: FULFILLED, INPROCESS, & DELIVERED.",
            sparklineData: generateSparklineData(paymentStatistics?.totalOrders || 0),
            color: POLARIS_COLORS.info,
        },
        {
            title: "Total Received",
            value: paymentStatistics?.totalReceivable || 0,
            format: (val: number) => `PKR ${val.toLocaleString()}`,
            tooltip: "Total receivable amount from paid and delivered orders",
            sparklineData: generateSparklineData(paymentStatistics?.totalReceivable || 0),
            color: POLARIS_COLORS.primary,
        },
        {
            title: "Total COD",
            value: paymentStatistics?.totalCOD || 0,
            format: (val: number) => `PKR ${val.toLocaleString()}`,
            tooltip: "Total Cash on Delivery amount",
            sparklineData: generateSparklineData(paymentStatistics?.totalCOD || 0),
            color: POLARIS_COLORS.info,
        },
        {
            title: "Avg Received per Order",
            value: paymentStatistics?.avgReceivablePerOrder || 0,
            format: (val: number) => `PKR ${val.toFixed(2)}`,
            tooltip: "Average received amount per paid order (Total Received / Paid Orders)",
            sparklineData: generateSparklineData(paymentStatistics?.avgReceivablePerOrder || 0),
            color: POLARIS_COLORS.primary,
        },
    ];

    const secondRowCards = [
        {
            title: "Paid Delivered Orders",
            value: paymentStatistics?.deliveredAndPaid || 0,
            format: (val: number) => val.toLocaleString(),
            tooltip: "Number of orders that are paid and delivered",
            sparklineData: generateSparklineData(paymentStatistics?.paidOrdersCount || 0),
            color: POLARIS_COLORS.primary,
        },
        {
            title: "Unpaid Delivered Orders",
            value: paymentStatistics?.deliveredAndUnpaid || 0,
            format: (val: number) => val.toLocaleString(),
            tooltip: "Number of orders that are unpaid and delivered",
            sparklineData: generateSparklineData(paymentStatistics?.paidOrdersCount || 0),
            color: POLARIS_COLORS.primary,
        },
        
    ];
    const thirdRowCards = [
        {
            title: "Unpaid Orders",
            value: paymentStatistics?.unpaidOrdersCount || 0,
            format: (val: number) => val.toLocaleString(),
            tooltip: "Number of orders that are unpaid",
            sparklineData: generateSparklineData(paymentStatistics?.unpaidOrdersCount || 0),
            color: POLARIS_COLORS.critical,
        },
        {
            title: "Total Paid Orders",
            value: paymentStatistics?.paidOrdersCount || 0,
            format: (val: number) => val.toLocaleString(),
            tooltip: "Number of orders that are paid",
            sparklineData: generateSparklineData(paymentStatistics?.paidOrdersCount || 0),
            color: POLARIS_COLORS.primary,
        },
        
    ];

    return (
        <Page titleHidden fullWidth compactTitle={true} >
            <div
                style={{
                    padding: "10px 0px",
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                }}
            >
                <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "auto" }}>
                    <div style={{ width: "auto" }}>
                        <DateRange />
                    </div>
                    {/* <CourierFilter /> */}
                </div>
            </div>
            <Grid columns={{ xs: 1, md: 2, lg: 12, xl: 12 }}>
                <Grid.Cell columnSpan={{ xs: 1, md: 2, lg: 12, xl: 12 }}>
                    <BlockStack gap={"400"}>
                        <Grid columns={{ xs: 1, md: 2, lg: 12, xl: 12 }}>
                            {firstRowCards.map((card) => (
                                <Grid.Cell key={card.title} columnSpan={{ xs: 6, md: 6, lg: 4, xl: 3 }}>
                                    <Card>
                                        <div
                                            style={{
                                                //   padding: "20px",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",

                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <div
                                                    style={{
                                                        borderBottom: "2px dotted rgb(184, 189, 197)",
                                                        padding: '0px',
                                                        paddingBottom: "4px",
                                                        marginBottom: "12px",
                                                        display: "inline-block",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <Tooltip content={card.tooltip}>
                                                        <Text as="h3" variant="bodyMd" fontWeight="medium">
                                                            {card.title}
                                                        </Text>
                                                    </Tooltip>
                                                </div>
                                                <div style={{ marginTop: "8px" }}>
                                                    {isLoading || isFetching ? (
                                                        <SkeletonDisplayText size="large" />
                                                    ) : isError ? (
                                                        <Text as="p" variant="headingXl" tone="critical">
                                                            Error
                                                        </Text>
                                                    ) : (
                                                        <Text as="p" variant="headingLg">
                                                            {card.format(card.value)}
                                                        </Text>
                                                    )}
                                                </div>
                                            </div>
                                            {!isLoading && !isFetching && !isError && (
                                                <div style={{ marginLeft: "16px" }}>
                                                    <MiniSparkline data={card.sparklineData} />
                                                </div>

                                            )}
                                        </div>

                                    </Card>
                                </Grid.Cell>
                            ))}
                        </Grid>
                        <Grid columns={{ xs: 1, md: 2, lg: 12, xl: 12 }}>
                            {secondRowCards.map((card) => (
                                <Grid.Cell key={card.title} columnSpan={{ xs: 6, md: 6, lg: 6, xl: 6 }}>
                                    <Card>
                                        <div
                                            style={{
                                                //   padding: "20px",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <div
                                                    style={{
                                                        borderBottom: "2px dotted rgb(184, 189, 197)",
                                                        padding: '0px',
                                                        paddingBottom: "4px",
                                                        marginBottom: "12px",
                                                        display: "inline-block",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <Tooltip content={card.tooltip}>
                                                        <Text as="h3" variant="bodyMd" fontWeight="medium">
                                                            {card.title}
                                                        </Text>
                                                    </Tooltip>
                                                </div>
                                                <div style={{ marginTop: "8px" }}>
                                                    {isLoading || isFetching ? (
                                                        <SkeletonDisplayText size="large" />
                                                    ) : isError ? (
                                                        <Text as="p" variant="headingXl" tone="critical">
                                                            Error
                                                        </Text>
                                                    ) : (
                                                        <Text as="p" variant="headingLg">
                                                            {card.format(card.value)}
                                                        </Text>
                                                    )}
                                                </div>
                                            </div>
                                            {!isLoading && !isFetching && !isError && (
                                                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                                                    <div style={{ marginLeft: "16px" }}>
                                                        <MiniSparkline data={card.sparklineData} />
                                                    </div>
                                                    <div style={{ marginTop: "8px", cursor: "pointer",  display: "flex", justifyContent: "flex-end" }}>
                                                        <Button icon={ViewIcon} variant="plain" onClick={() => {
                                                            navigateTo(`/dashboard/orders?bookingfrom=${startDate}&bookingto=${endDate}&page=1&payment_status=${card.title === "Paid Delivered Orders" ? "PAID" : "UNPAID"}&status=DELIVERED`);
                                                        }}>
                                                            View
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </Grid.Cell>
                            ))}
                        </Grid>
                        <Grid columns={{ xs: 1, md: 2, lg: 12, xl: 12 }}>
                            {thirdRowCards.map((card) => (
                                <Grid.Cell key={card.title} columnSpan={{ xs: 6, md: 6, lg: 6, xl: 6 }}>
                                    <Card>
                                        <div
                                            style={{
                                                //   padding: "20px",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <div
                                                    style={{
                                                        borderBottom: "2px dotted rgb(184, 189, 197)",
                                                        padding: '0px',
                                                        paddingBottom: "4px",
                                                        marginBottom: "12px",
                                                        display: "inline-block",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <Tooltip content={card.tooltip}>
                                                        <Text as="h3" variant="bodyMd" fontWeight="medium">
                                                            {card.title}
                                                        </Text>
                                                    </Tooltip>
                                                </div>
                                                <div style={{ marginTop: "8px" }}>
                                                    {isLoading || isFetching ? (
                                                        <SkeletonDisplayText size="large" />
                                                    ) : isError ? (
                                                        <Text as="p" variant="headingXl" tone="critical">
                                                            Error
                                                        </Text>
                                                    ) : (
                                                        <Text as="p" variant="headingLg">
                                                            {card.format(card.value)}
                                                        </Text>
                                                    )}
                                                </div>
                                            </div>
                                            {!isLoading && !isFetching && !isError && (
                                                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                                                    <div style={{ marginLeft: "16px" }}>
                                                        <MiniSparkline data={card.sparklineData} />
                                                    </div>
                                                    <div style={{ marginTop: "8px", cursor: "pointer",  display: "flex", justifyContent: "flex-end" }}>
                                                        <Button icon={ViewIcon} variant="plain" onClick={() => {
                                                            navigateTo(`/dashboard/orders?bookingfrom=${startDate}&bookingto=${endDate}&page=1&payment_status=${card.title === "Total Paid Orders" ? "PAID" : "UNPAID"}`);
                                                        }}>
                                                            View
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </Grid.Cell>
                            ))}
                        </Grid>
                    </BlockStack>
                </Grid.Cell>
                {/* <Grid.Cell columnSpan={{ xs: 1, md: 6, lg: 4, xl: 4 }}>
                    <BlockStack gap={"200"}>
                        <Card>
                            <BlockStack gap={"200"}>
                                <div
                                    style={{
                                        borderBottom: "1px dotted #6b7280",
                                        //   paddingBottom: "4px",
                                        //   marginBottom: "12px",
                                        display: "inline-block",
                                        cursor: "pointer",
                                        width: "max-content",
                                    }}
                                >
                                    <Tooltip content={"hello"}>
                                        <Text as="h3" variant="bodyMd" fontWeight="medium">
                                            Total Receivable
                                        </Text>
                                    </Tooltip>
                                </div>
                            </BlockStack>
                        </Card>
                    </BlockStack>
                </Grid.Cell> */}
            </Grid>

        </Page>
    );
}